import { useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useUser } from "../context/UserContext";
import NotificationBell from "./NotificationBell";

export default function Navbar() {
  const { user, setUser } = useUser();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const logout = () => {
    setUser(null);
    localStorage.removeItem("jwt");
    localStorage.removeItem("user");
    navigate("/");
  };

  const toggleDropdown = () => {
    if (dropdownRef.current) {
      dropdownRef.current.classList.toggle("hidden");
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        dropdownRef.current.classList.add("hidden");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Couleurs de rangs harmonisées et plus intenses
  const getBorderClass = (level: string | undefined) => {
    switch (level) {
      case "bronze": return "border-2 border-amber-600 shadow-[0_0_10px_rgba(180,83,9,0.3)]";
      case "argent": return "border-2 border-zinc-400 shadow-[0_0_10px_rgba(161,161,170,0.3)]";
      case "or": return "border-2 border-yellow-400 shadow-[0_0_12px_rgba(234,179,8,0.5)]";
      case "platine": return "border-2 border-sky-300 shadow-[0_0_12px_rgba(125,211,252,0.4)]";
      case "diamant": return "border-2 border-cyan-400 shadow-[0_0_15px_rgba(34,211,238,0.5)]";
      case "maître": return "border-2 border-purple-500 shadow-[0_0_15px_rgba(168,85,247,0.5)]";
      case "légende": return "border-2 border-pink-500 shadow-[0_0_15px_rgba(236,72,153,0.6)] animate-pulse";
      case "immortel": return "border-2 border-red-500 shadow-[0_0_20px_rgba(239,68,68,0.7)] animate-pulse";
      default: return "border-2 border-zinc-700";
    }
  };

  return (
    <nav className="relative z-50 mx-auto mt-4 max-w-6xl px-4">
      {/* Conteneur principal en mode "Flottant Translucide" */}
      <div className="rounded-2xl border border-zinc-800/80 bg-zinc-950/40 backdrop-blur-md shadow-[0_15px_50px_-20px_rgba(0,0,0,0.7)]">
        <div className="flex items-center justify-between px-5 py-3">
          
          {/* Logo & Branding */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="relative">
              <img src="/logo-64.webp" width="40" height="32" className="h-8 w-10 rounded-lg object-contain transition duration-300 group-hover:scale-105" alt="Kettlecore Sync Logo" />
              <div className="absolute -inset-1 rounded-lg bg-yellow-400/10 blur-sm opacity-0 group-hover:opacity-100 transition duration-300" />
            </div>
            <div className="flex flex-col leading-none">
              <span className="text-base font-black tracking-tight text-white uppercase group-hover:text-yellow-400 transition duration-200">
                Kettlecore <span className="text-yellow-400 group-hover:text-white">Sync</span>
              </span>
              <span className="text-[9px] uppercase tracking-widest text-zinc-500 mt-0.5">
                Performance Lab
              </span>
            </div>
          </Link>

          {/* Actions & Profil */}
          <div className="relative flex items-center gap-3">
            {user && (
              <>
                <NotificationBell />
                
                {/* Bouton Avatar avec Glow de Rang */}
                <button
                  type="button"
                  className="group relative inline-flex items-center justify-center rounded-full outline-none transition cursor-pointer"
                  onClick={toggleDropdown}
                  aria-haspopup="menu"
                  aria-expanded="false"
                >
                  <img
                    className={`h-9 w-9 rounded-full object-cover p-[1px] bg-zinc-950 transition duration-300 group-hover:scale-105 ${getBorderClass(user?.streak_level)}`}
                    src={user?.picture || "/logo-64.webp"}
                    alt="user avatar"
                    onError={(e) => {
                      const img = e.currentTarget as HTMLImageElement;
                      img.onerror = null;
                      img.src = "/logo-64.webp";
                    }}
                  />
                </button>

                {/* Dropdown Menu Minimaliste & Tech */}
                <div
                  ref={dropdownRef}
                  className="hidden z-50 w-auto max-w-[calc(100vw-32px)] overflow-hidden rounded-xl border border-zinc-800 bg-zinc-950/95 backdrop-blur-xl shadow-[0_20px_50px_rgba(0,0,0,0.9)] fixed inset-x-4 top-20 md:absolute md:inset-auto md:right-0 md:top-12 md:w-64"
                  role="menu"
                  aria-label="Menu utilisateur"
                >
                  {/* Profil Header */}
                  <div className="relative px-4 py-4 bg-zinc-900/20">
                    <div className="absolute top-0 right-0 h-16 w-16 rounded-full bg-yellow-500/5 blur-xl pointer-events-none" />
                    <span className="block truncate text-sm font-bold text-white">{user?.name}</span>
                    <span className="block truncate text-xs text-zinc-400 mt-0.5">{user?.email}</span>
                  </div>

                  <div className="h-px w-full bg-zinc-900" />

                  {/* Liste des Liens */}
                  <ul className="p-1.5 space-y-0.5">
                    
                    {/* ——— Section ADMIN ——— */}
                    {user?.admin && (
                      <>
                        <li className="px-2 pt-1.5 pb-0.5">
                          <span className="text-[9px] font-bold uppercase tracking-widest text-yellow-500/80">
                            Console Admin
                          </span>
                        </li>
                        <li>
                          <Link
                            to="/admin"
                            role="menuitem"
                            className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-xs font-bold text-yellow-400 bg-yellow-500/5 border border-yellow-500/10 hover:bg-yellow-500/10 hover:border-yellow-400/20 transition"
                            onClick={() => dropdownRef.current?.classList.add("hidden")}
                          >
                            🛡️ Espace Supervision
                          </Link>
                        </li>
                        <li className="py-1">
                          <div className="h-px w-full bg-zinc-900" />
                        </li>
                      </>
                    )}

                    {/* ——— Section NAVIGATION ——— */}
                    <li className="px-2 pt-1 pb-0.5">
                      <span className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">
                        Navigation
                      </span>
                    </li>
                    <li>
                      <Link
                        to="/profile"
                        role="menuitem"
                        className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-xs font-medium text-zinc-300 hover:bg-zinc-900 hover:text-white transition"
                        onClick={() => dropdownRef.current?.classList.add("hidden")}
                      >
                        👤 Mon profil
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/documents"
                        role="menuitem"
                        className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-xs font-medium text-zinc-300 hover:bg-zinc-900 hover:text-white transition"
                        onClick={() => dropdownRef.current?.classList.add("hidden")}
                      >
                        📂 Ressources PDF
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/exercices"
                        role="menuitem"
                        className="flex items-center justify-between rounded-lg px-2.5 py-2 text-xs font-medium text-zinc-300 hover:bg-zinc-900 hover:text-white transition"
                        onClick={() => dropdownRef.current?.classList.add("hidden")}
                      >
                        <span className="flex items-center gap-2">🤖 Analyse Exercices</span>
                        <span className="rounded-md border border-violet-500/30 bg-violet-500/10 px-1.5 py-0.5 text-[9px] font-bold text-violet-400 uppercase tracking-wide">
                          Beta IA
                        </span>
                      </Link>
                    </li>

                    <li className="py-1">
                      <div className="h-px w-full bg-zinc-900" />
                    </li>

                    {/* ——— Section SÉCURITÉ / COMPTE ——— */}
                    <li>
                      <button
                        onClick={logout}
                        role="menuitem"
                        className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-xs font-semibold text-red-400 hover:bg-red-500/5 transition"
                      >
                        🚪 Déconnexion
                      </button>
                    </li>
                  </ul>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}
